import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncBlock<T> {
  data?: T;
  isLoading: boolean;
  hasError: boolean;
  retry: () => void;
}

interface BlockState<T> {
  data?: T;
  isLoading: boolean;
  hasError: boolean;
}

interface AsyncBlockOptions {
  keepPreviousData?: boolean;
}

const useAsyncBlock = <T>(load: () => Promise<T>, { keepPreviousData = false }: AsyncBlockOptions = {}): AsyncBlock<T> => {
  const [state, setState] = useState<BlockState<T>>({ isLoading: true, hasError: false });
  const latestRequestId = useRef(0);

  const fetchBlock = useCallback(async (): Promise<void> => {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isLatest = (): boolean => requestId === latestRequestId.current;

    setState((previous) => ({ data: keepPreviousData ? previous.data : undefined, isLoading: true, hasError: false }));
    try {
      const data = await load();
      if (isLatest()) setState({ data, isLoading: false, hasError: false });
    } catch {
      if (isLatest()) setState({ isLoading: false, hasError: true });
    }
  }, [load, keepPreviousData]);

  useEffect(() => {
    fetchBlock();
  }, [fetchBlock]);

  return { ...state, retry: fetchBlock };
};

export default useAsyncBlock;
